/**
 * Stripe Credit Note v2 — frontend types + eligibility fetcher.
 *
 * Mirrors the backend payload from `StripePaymentService::getInvoiceCreditEligibility()`
 * and the controller endpoint
 * `GET customer-orders/{order_id}/invoices/{order_item_invoice_id}/credit-note-eligibility`.
 *
 * Used by Task 16169 (split button + invoice picker) and Task 16178 (wizard).
 */

import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';

export type CreditNoteDisableReason = 'voided' | 'draft' | 'fully_credited' | null;
export type CreditNotePaymentPath = 'card_refund' | 'out_of_band' | 'unpaid' | null;

export interface CreditNoteInvoiceLine {
  id: string;
  description: string | null;
  quantity: number;
  unit_amount_minor: number;
  amount_minor: number;
  currency: string;
  /** Sum of `quantity` across all existing credit notes for this `il_xxx`. */
  already_credited_qty: number;
  /** Sum of `amount` (minor units) across all existing credit notes for this `il_xxx`. */
  already_credited_amount_minor: number;
  /** `quantity - already_credited_qty`, clamped to ≥ 0. */
  remaining_qty: number;
  /** `amount_minor - already_credited_amount_minor`, clamped to ≥ 0. */
  remaining_amount_minor: number;
}

export interface CreditNoteExistingCnSummary {
  id: string;
  number: string | null;
  amount_minor: number;
  status: string | null;
  created: number;
  reason: string | null;
  memo: string | null;
  pdf: string | null;
}

export interface CreditNoteInvoiceSummary {
  stripe_invoice_id: string;
  status: string;
  total_minor: number;
  amount_paid_minor: number;
  amount_remaining_minor: number;
  pre_payment_credit_notes_amount: number;
  post_payment_credit_notes_amount: number;
}

export interface CreditNoteEligibility {
  can_credit: boolean;
  disable_reason: CreditNoteDisableReason;
  remaining_creditable_minor: number;
  remaining_creditable: number;
  currency: string;
  payment_path: CreditNotePaymentPath;
  invoice: CreditNoteInvoiceSummary;
  lines: CreditNoteInvoiceLine[];
  existing_credit_notes: CreditNoteExistingCnSummary[];
}

/**
 * Fetches Stripe credit-note eligibility + lines for one ledger row (order_item_invoices.id).
 * Throws on network/Stripe failure (caller is expected to surface the error to the user).
 */
export async function fetchInvoiceCreditEligibility(
  orderId: number,
  orderItemInvoiceId: number,
): Promise<CreditNoteEligibility> {
  try {
    // Backend `sendJsonResponse($message, $data)` wraps the payload under `result` (NOT `data`).
    // Shape: `{ message: string, result: { eligibility: ... }, errors: [] }`.
    const response = await http.get<{ message: string; result: { eligibility: CreditNoteEligibility } }>(
      `customer-orders/${orderId}/invoices/${orderItemInvoiceId}/credit-note-eligibility`,
    );
    return response.data.result.eligibility;
  } catch (err) {
    handleResponseException(err);
    throw err;
  }
}

/**
 * Human-readable label for a disable reason — used for tooltips and toasts.
 */
export function disableReasonLabel(reason: CreditNoteDisableReason): string {
  switch (reason) {
    case 'voided':
      return 'Invoice was voided.';
    case 'draft':
      return 'Invoice is not finalized yet.';
    case 'fully_credited':
      return 'Invoice is already fully credited.';
    default:
      return '';
  }
}

/**
 * Convert minor units → display major units using the same zero-decimal-currency list as
 * `StripePaymentService::amountFromStripeMinorUnits()` so frontend totals match the backend.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

export function minorUnitsToAmount(minor: number, currency: string): number {
  if (ZERO_DECIMAL_CURRENCIES.has((currency || '').toUpperCase())) {
    return Number(minor);
  }
  return Math.round((Number(minor) / 100) * 100) / 100;
}

/**
 * Inverse of {@link minorUnitsToAmount}: display major-unit value → Stripe minor units, with
 * the same zero-decimal-currency awareness as the backend `amountToStripeMinorUnits()`.
 * NaN / non-finite inputs round-trip to 0.
 */
export function amountToMinorUnits(major: number, currency: string): number {
  const value = Number(major);
  if (!Number.isFinite(value)) return 0;
  if (ZERO_DECIMAL_CURRENCIES.has((currency || '').toUpperCase())) {
    return Math.max(0, Math.round(value));
  }
  return Math.max(0, Math.round(value * 100));
}

/**
 * Format minor units → human display string `"1,234.56"` (no currency code suffix; caller appends).
 * Zero-decimal currencies skip the decimal part.
 */
export function formatMinorAsDisplay(minor: number, currency: string): string {
  const amount = minorUnitsToAmount(minor, currency);
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has((currency || '').toUpperCase());
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: isZeroDecimal ? 0 : 2,
    maximumFractionDigits: isZeroDecimal ? 0 : 2,
  });
}

/** Mirrors the Stripe enum allowed by the backend. */
export const CREDIT_NOTE_REASONS = [
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'order_change', label: 'Order change' },
  { value: 'product_unsatisfactory', label: 'Product unsatisfactory' },
] as const;

export type CreditNoteReason = typeof CREDIT_NOTE_REASONS[number]['value'];

/**
 * Stable display copy keyed by `payment_path`. Used by the wizard's inline success view + by the
 * fallback toast path. Card-paid never shows the finance@ message; non-card paid always does;
 * unpaid never mentions refunds (per US 5253 / Task 16179 AC).
 */
export function paymentPathOutcomeMessage(path: string | null | undefined): string {
  switch (path) {
    case 'card_refund':
      return 'Credit note created. Refund issued to the original card.';
    case 'out_of_band':
      return 'Credit note created. Send a message to finance@custimoo.com if we should do a refund.';
    case 'unpaid':
      return 'Credit note applied. The invoice’s amount due has been reduced.';
    default:
      return 'Credit note created.';
  }
}

/**
 * Heuristic: detect when a backend / Stripe error during credit-note creation is a refund-side
 * failure (charge too old, partially refunded already, payment source no longer valid, etc.).
 * Used by the wizard's inline error view to surface actionable guidance instead of a generic
 * Stripe error toast.
 */
export function isLikelyRefundFailure(
  errorMessage: string | null | undefined,
  paymentPath: string | null | undefined,
): boolean {
  if (paymentPath !== 'card_refund') return false;
  if (!errorMessage) return false;
  const msg = errorMessage.toLowerCase();
  return (
    msg.includes('refund')
    || msg.includes('charge_disputed')
    || msg.includes('charge too old')
    || msg.includes('partially refunded')
    || msg.includes('refund_amount')
    || msg.includes('payment source')
  );
}

/**
 * Extract the best-available human-readable error message from an axios error (handles the
 * Laravel `sendJsonResponse` shape with `message` + `errors[]`, plus generic axios fallbacks).
 */
export function extractCreditNoteErrorMessage(error: unknown): string {
  if (!error) return 'Credit note creation failed.';
  const e = error as {
    response?: { data?: { message?: string; errors?: unknown } };
    message?: string;
  };
  const data = e.response?.data;
  if (data?.message) {
    let detail = String(data.message);
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = data.errors[0];
      if (typeof first === 'string') detail += ` — ${first}`;
    } else if (data.errors && typeof data.errors === 'object') {
      const firstKey = Object.keys(data.errors as Record<string, unknown>)[0];
      if (firstKey) {
        const val = (data.errors as Record<string, unknown>)[firstKey];
        if (Array.isArray(val) && typeof val[0] === 'string') detail += ` — ${val[0]}`;
        else if (typeof val === 'string') detail += ` — ${val}`;
      }
    }
    return detail;
  }
  if (e.message) return e.message;
  return 'Credit note creation failed.';
}
